// libraries
import type { SkeletonProps } from '@interstate/components/Skeleton';
import { Skeleton } from '@interstate/components/Skeleton';

// styles
import { StyledCardHeadline, StyledCardList, StyledFieldRow, StyledSectionHeader } from './PurchaseHistorySection.style';

type SkeletonVariant = SkeletonProps['variant'];

const headerSkeleton: SkeletonVariant = { type: 'rounded', width: 160, height: 19 };
const headlineSkeleton: SkeletonVariant = { type: 'rounded', width: 152, height: 19 };
const badgeSkeleton: SkeletonVariant = { type: 'rounded', width: 98, height: 24 };
const fieldSkeleton: SkeletonVariant = { type: 'rounded', width: 74, height: 42 };

const PLACEHOLDER_CARD_COUNT = 2;
const PLACEHOLDER_FIELD_COUNT = 7;

/**
 * The loading treatment: placeholders shaped like the cards they will be replaced by, rather than a
 * bare spinner, so the panel does not visibly reflow when the data lands.
 *
 * `aria-busy` and the accessible label matter here — without them a screen reader user gets silence
 * while the section is pending.
 */
export const PurchaseHistorySkeleton = () => (
    <div data-testid="purchase-history-skeleton" aria-busy="true" aria-label="Loading purchase history">
        <StyledSectionHeader>
            <Skeleton variant={headerSkeleton} />
        </StyledSectionHeader>
        <StyledCardList>
            {Array.from({ length: PLACEHOLDER_CARD_COUNT }).map((_placeholder, cardIndex) => (
                <div key={cardIndex}>
                    <StyledCardHeadline>
                        <Skeleton variant={headlineSkeleton} />
                        <Skeleton variant={badgeSkeleton} />
                    </StyledCardHeadline>
                    <StyledFieldRow>
                        {Array.from({ length: PLACEHOLDER_FIELD_COUNT }).map((_field, fieldIndex) => (
                            <Skeleton key={fieldIndex} variant={fieldSkeleton} />
                        ))}
                    </StyledFieldRow>
                </div>
            ))}
        </StyledCardList>
    </div>
);

export default PurchaseHistorySkeleton;
